import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  Droppable,
  DroppableProvided,
} from "@hello-pangea/dnd";
import { Delete, DragIndicator, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useState } from "react";

interface Caretaker {
  id: number;
  relation: string;
  name: string;
  contact: string;
  email: string;
}
interface DraggableCaretakerRowProps {
  caretaker: Caretaker;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  onDelete: () => void;
}

export const DraggableCaretakerRow: React.FC<DraggableCaretakerRowProps> = ({
  caretaker: c,
  provided,
  snapshot,
  onDelete,
}) => (
  <TableRow
    ref={provided.innerRef}
    {...provided.draggableProps}
    style={{
      backgroundColor: snapshot.isDragging ? "#f0f0f0" : "inherit",
      ...provided.draggableProps.style,
    }}
  >
    <TableCell {...provided.dragHandleProps}>
      <DragIndicator sx={{ color: "gray" }} />
    </TableCell>
    <TableCell>{c.relation}</TableCell>
    <TableCell>{c.name}</TableCell>
    <TableCell>{c.contact}</TableCell>
    <TableCell>{c.email}</TableCell>
    <TableCell>
      <IconButton color="primary" size="small">
        <Edit fontSize="small" />
      </IconButton>
      <IconButton color="error" size="small" onClick={onDelete}>
        <Delete fontSize="small" />
      </IconButton>
    </TableCell>
  </TableRow>
);

export const CaretakerLayout = () => {
  const [caretakers, setCaretakers] = useState<Caretaker[]>([
    {
      id: 1,
      relation: "Spouse",
      name: "James Cooper",
      contact: "+1 222 453 6709",
      email: "jamesc@gmail.com",
    },
    {
      id: 2,
      relation: "Mother",
      name: "Lilli Thomas",
      contact: "+1 222 435 5656",
      email: "lilli.thomas@gmail.com",
    },
    {
      id: 3,
      relation: "Relative",
      name: "John Thomas",
      contact: "+1 220 145 5346",
      email: "john.thomas@gmail.com",
    },
  ]);

  const [formData, setFormData] = useState({
    relation: "",
    name: "",
    contact: "",
    email: "",
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const name = e.target.name;
    const value = e.target.value ;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    if (
      !formData.relation ||
      !formData.name ||
      !formData.contact ||
      !formData.email
    )
      return;

    const newCaretaker: Caretaker = {
      id: caretakers.length + 1,
      relation: formData.relation,
      name: formData.name,
      contact: formData.contact,
      email: formData.email,
    };

    setCaretakers((prev) => [...prev, newCaretaker]);
    setFormData({ relation: "", name: "", contact: "", email: "" });
  };

  const handleDelete = (id: number) => {
    setCaretakers((prev) => prev.filter((c) => c.id !== id));
  };

  const deleteHandlers = () => {
    const map = new Map<number, () => void>();
    caretakers.forEach((c) => map.set(c.id, () => handleDelete(c.id)));
    return map;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(caretakers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setCaretakers(items);
    console.log("📤 Updated priority order:", items);
  };




  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Caretakers
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Add Caretaker
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You can add up to 3 caretakers
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Relation</InputLabel>
              <Select
                name="relation"
                value={formData.relation}
                label="Relation"
                onChange={handleChange}
              >
                <MenuItem value="Spouse">Spouse</MenuItem>
                <MenuItem value="Mother">Mother</MenuItem>
                <MenuItem value="Father">Father</MenuItem>
                <MenuItem value="Relative">Relative</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ minWidth: 200 }}
            />

            <TextField
              label="Contact Number"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              sx={{ minWidth: 200 }}
            />

            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              sx={{ minWidth: 200 }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              disabled={caretakers.length >= 3}
              sx={{ height: 56 }}
            >
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Total Caretakers: {caretakers.length}
          </Typography>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="caretakerTable">
              {(provided: DroppableProvided) => (
                <Table
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ borderCollapse: "collapse" }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Relation</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {caretakers.map((c, index) => (
                      <Draggable
                        key={c.id.toString()}
                        draggableId={c.id.toString()}
                        index={index}
                      >
                        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              backgroundColor: snapshot.isDragging
                                ? "#f0f0f0"
                                : "inherit",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <TableCell {...provided.dragHandleProps}>
                              <DragIndicator sx={{ color: "gray" }} />
                            </TableCell>
                            <TableCell>{c.relation}</TableCell>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.contact}</TableCell>
                            <TableCell>{c.email}</TableCell>
                            <TableCell>
                              <IconButton color="primary" size="small">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={deleteHandlers().get(c.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              )}
            </Droppable>
          </DragDropContext>
        </CardContent>
      </Card>
    </Box>
  );
};
